import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Grid2x2,
    Loader2,
    Maximize2,
    Minus,
    Square,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useBatchProcessing } from '../contexts/BatchProcessingContext';
import { useProductsApi } from '../common/hooks/use-products-api.hook';

/** One of the three job filters, styled as a solid pill when it is the active one. */
const FilterPill = ({
    label,
    active,
    tone = 'default',
    onClick,
}: {
    label: string;
    active: boolean;
    tone?: 'default' | 'destructive';
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            'h-6.5 border px-2 text-xs font-medium transition-colors',
            active
                ? tone === 'destructive'
                    ? 'border-destructive bg-destructive text-white'
                    : 'border-foreground bg-foreground text-background'
                : 'border-input text-muted-foreground hover:bg-accent',
        )}
    >
        {label}
    </button>
);

export const BatchProcessingPanel: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        batchStatus,
        isBatchProcessing,
        batchComplete,
        isPanelMinimized,
        setIsPanelMinimized,
        isPanelExpanded,
        setIsPanelExpanded,
        isPanelVisible,
        jobListFilter,
        setJobListFilter,
        isAborting,
        setIsAborting,
        closePanel
    } = useBatchProcessing();

    const { abortBulkBatch } = useProductsApi();

    const handleAbortBatch = async () => {
        if (!batchStatus?.batchId || isAborting) return;

        setIsAborting(true);
        try {
            await abortBulkBatch(batchStatus.batchId);
        } catch (error) {
            console.error('Error aborting batch:', error);
        } finally {
            setIsAborting(false);
        }
    };

    // Don't render if not visible
    if (!isPanelVisible || !batchStatus) {
        return null;
    }

    // Minimized state
    if (isPanelMinimized) {
        return (
            <button
                type="button"
                onClick={() => setIsPanelMinimized(false)}
                className="fixed bottom-[calc(1.5rem+var(--support-fab-clearance,0px))] end-6 z-[1300] flex items-center gap-3 border bg-background px-5 py-3 shadow-sm transition-shadow hover:shadow-md"
            >
                {isBatchProcessing ? (
                    <Loader2 className="size-4.5 animate-spin" />
                ) : batchStatus.failed > 0 ? (
                    <AlertCircle className="size-4.5 text-destructive" />
                ) : (
                    <CheckCircle2 className="size-4.5 text-success" />
                )}
                <span className="text-sm font-medium">
                    {isBatchProcessing
                        ? `${t('PAGES.BATCH_PROCESSING.PROCESSING')}... ${batchStatus.percentage}%`
                        : t('PAGES.BATCH_PROCESSING.PROCESSING_COMPLETE')}
                </span>
                <Maximize2 className="size-4 text-muted-foreground" />
            </button>
        );
    }

    const jobs = batchStatus.jobs || [];

    return (
        <div className="fixed bottom-[calc(1.5rem+var(--support-fab-clearance,0px))] end-6 z-[1300] flex max-h-[70vh] w-[380px] flex-col overflow-hidden border bg-background shadow-md">
            {/* Header */}
            <div className="flex items-center justify-between border-b bg-muted px-4 py-3">
                <div className="flex items-center gap-2">
                    <Grid2x2 className="size-5" />
                    <p className="text-base font-medium">
                        {isBatchProcessing
                            ? t('PAGES.BATCH_PROCESSING.PROCESSING_PRODUCTS')
                            : batchComplete
                              ? t('PAGES.BATCH_PROCESSING.PROCESSING_COMPLETE')
                              : t('PAGES.BATCH_PROCESSING.BATCH_STATUS')}
                    </p>
                </div>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setIsPanelMinimized(true)}
                        aria-label={t('COMMON.MINIMIZE', 'Minimize') as string}
                    >
                        <Minus className="size-4" />
                    </Button>
                    {/* Closing mid-run would hide a job the vendor cannot get back to. */}
                    {!isBatchProcessing && (
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={closePanel}
                            aria-label={t('COMMON.CLOSE') as string}
                        >
                            <X className="size-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Progress */}
            <div className="border-b px-4 py-3">
                <div className="mb-2 flex items-center justify-between text-sm">
                    <span>
                        {batchStatus.completed + batchStatus.failed} / {batchStatus.total}{' '}
                        {t('PAGES.BATCH_PROCESSING.PRODUCTS')}
                    </span>
                    <span className="font-medium">{batchStatus.percentage}%</span>
                </div>

                <Progress
                    value={batchStatus.percentage}
                    className={cn(
                        'h-1.5',
                        batchStatus.failed > 0 && !isBatchProcessing
                            ? '[&_[data-slot=progress-indicator]]:bg-destructive'
                            : batchComplete
                              ? '[&_[data-slot=progress-indicator]]:bg-success'
                              : '[&_[data-slot=progress-indicator]]:bg-foreground',
                    )}
                />

                <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                        <CheckCircle2 className="size-3.5 text-success" />
                        {batchStatus.completed} {t('PAGES.BATCH_PROCESSING.COMPLETED').toLowerCase()}
                    </span>
                    {(batchStatus.generating_seamless > 0 || batchStatus.processing > 0) && (
                        <span className="flex items-center gap-1">
                            <Loader2 className="size-3 animate-spin" />
                            {batchStatus.generating_seamless + batchStatus.processing}{' '}
                            {t('PAGES.BATCH_PROCESSING.PROCESSING').toLowerCase()}
                        </span>
                    )}
                    {batchStatus.pending > 0 && (
                        <span className="flex items-center gap-1">
                            <span className="size-2.5 rounded-full bg-border" />
                            {batchStatus.pending} {t('PAGES.BATCH_PROCESSING.PENDING').toLowerCase()}
                        </span>
                    )}
                    {batchStatus.failed > 0 && (
                        <span className="flex items-center gap-1 text-destructive">
                            <AlertCircle className="size-3.5" />
                            {batchStatus.failed} {t('PAGES.BATCH_PROCESSING.FAILED').toLowerCase()}
                        </span>
                    )}
                </div>

                {isBatchProcessing && (
                    <Button
                        onClick={handleAbortBatch}
                        disabled={isAborting}
                        variant="outline"
                        size="sm"
                        className="mt-3 w-full border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                    >
                        {isAborting ? <Loader2 className="size-3.5 animate-spin" /> : <Square className="size-3.5" />}
                        {isAborting ? t('PAGES.BATCH_PROCESSING.ABORTING') : t('PAGES.BATCH_PROCESSING.ABORT_PROCESSING')}
                    </Button>
                )}
            </div>

            {/* Filters */}
            {jobs.length > 0 && (
                <div className="flex gap-2 border-b px-4 py-2">
                    <FilterPill
                        label={t('PAGES.BATCH_PROCESSING.ALL')}
                        active={jobListFilter === 'all'}
                        onClick={() => setJobListFilter('all')}
                    />
                    <FilterPill
                        label={t('PAGES.BATCH_PROCESSING.COMPLETED')}
                        active={jobListFilter === 'completed'}
                        onClick={() => setJobListFilter('completed')}
                    />
                    <FilterPill
                        label={t('PAGES.BATCH_PROCESSING.FAILED')}
                        active={jobListFilter === 'failed'}
                        tone="destructive"
                        onClick={() => setJobListFilter('failed')}
                    />
                </div>
            )}

            {/* Job list */}
            {isPanelExpanded && jobs.length > 0 && (
                <div className="max-h-[35vh] overflow-y-auto">
                    {jobs
                        .filter(job =>
                            jobListFilter === 'all' ? true :
                            jobListFilter === 'completed' ? job.status === 'completed' :
                            job.status === 'failed'
                        )
                        .map((job, index) => (
                            <div
                                key={job._id || index}
                                className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 transition-colors last:border-b-0 hover:bg-accent"
                            >
                                <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden border bg-muted">
                                    {job.resultProduct?.thumbnailPath ? (
                                        <img
                                            src={job.resultProduct.thumbnailPath}
                                            alt={job.productData?.name || 'Product'}
                                            className="size-full object-cover"
                                        />
                                    ) : (
                                        <Grid2x2 className="size-4 text-muted-foreground" />
                                    )}
                                </span>

                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-xs font-medium">
                                        {job.productData?.name || 'Unknown Product'}
                                    </p>
                                    {job.status === 'failed' && job.error && (
                                        <p className="truncate text-[11px] text-destructive">
                                            {job.errorDetails?.userMessage || job.error}
                                        </p>
                                    )}
                                </div>

                                {job.status === 'completed' ? (
                                    <CheckCircle2 className="size-4 shrink-0 text-success" />
                                ) : job.status === 'failed' ? (
                                    <AlertCircle className="size-4 shrink-0 text-destructive" />
                                ) : job.status === 'generating_seamless' || job.status === 'processing' ? (
                                    <Loader2 className="size-3.5 shrink-0 animate-spin" />
                                ) : (
                                    <span className="size-3.5 shrink-0 rounded-full border-2 border-border" />
                                )}
                            </div>
                        ))}
                </div>
            )}

            {/* Expand / collapse */}
            {jobs.length > 0 && (
                <button
                    type="button"
                    onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                    className="flex items-center justify-center gap-1 border-t py-2 text-xs text-muted-foreground transition-colors hover:bg-accent"
                >
                    {isPanelExpanded ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
                    {isPanelExpanded ? t('PAGES.BATCH_PROCESSING.COLLAPSE') : t('PAGES.BATCH_PROCESSING.EXPAND')} (
                    {jobs.length} {t('PAGES.BATCH_PROCESSING.ITEMS')})
                </button>
            )}

            {batchComplete && (
                <div className="border-t p-4">
                    <Button className="w-full" onClick={() => navigate('/products/catalog')}>
                        {t('PAGES.BATCH_PROCESSING.BACK_TO_CATALOG')}
                    </Button>
                </div>
            )}
        </div>
    );
};
